import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import type { EmployerReviewPanelState } from '../../lib/employerReview/employerReviewPanelState';

export function EmployerReviewPanelStatus({
  state,
  onRetry,
  retrying,
  devDiagnostics,
}: {
  state: EmployerReviewPanelState;
  onRetry?: () => void;
  retrying?: boolean;
  /** Dev-only raw diagnostics — never shown in production builds. */
  devDiagnostics?: string[];
}) {
  if (state.loadState === 'ok' || state.issues.length === 0) {
    return null;
  }

  const severity = state.loadState === 'failed' ? 'error' : 'warning';

  return (
    <Alert
      severity={severity}
      role="alert"
      aria-live="polite"
      sx={{
        color: 'text.primary',
        '& .MuiAlert-icon': { color: 'inherit', opacity: 0.9 },
        '& .MuiAlert-message': { width: '100%', color: 'text.primary' },
      }}
      action={
        state.retryable && onRetry ? (
          <Button
            color="inherit"
            size="small"
            onClick={onRetry}
            disabled={retrying}
            startIcon={retrying ? <CircularProgress size={14} color="inherit" /> : <RefreshRoundedIcon />}
            sx={{ fontWeight: 600, whiteSpace: 'nowrap', minHeight: 36 }}
          >
            {retrying ? 'Retrying…' : 'Retry'}
          </Button>
        ) : undefined
      }
    >
      <AlertTitle sx={{ fontWeight: 700, mb: 0.25 }}>{state.headline}</AlertTitle>
      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.45 }}>
        {state.detail}
      </Typography>
      {state.correlationId ? (
        <Typography
          variant="caption"
          component="p"
          sx={{ mt: 0.75, color: 'text.secondary', fontFamily: 'monospace', wordBreak: 'break-all' }}
        >
          Reference: {state.correlationId}
        </Typography>
      ) : null}
      {import.meta.env.DEV && devDiagnostics && devDiagnostics.length > 0 ? (
        <Box component="details" sx={{ mt: 1 }}>
          <Typography component="summary" variant="caption" sx={{ cursor: 'pointer', color: 'text.disabled' }}>
            Developer diagnostics
          </Typography>
          <Stack component="pre" spacing={0.5} sx={{ mt: 0.5, fontSize: 11, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
            {devDiagnostics.map((line) => (
              <Box key={line}>{line}</Box>
            ))}
          </Stack>
        </Box>
      ) : null}
    </Alert>
  );
}
